import { useEffect, useRef, useState } from 'react'
import { Box, TextField, ToggleButton, ToggleButtonGroup, Alert, FormControlLabel, Checkbox, Tooltip, Typography, Divider } from '@mui/material'
import { ArrowForwardIosSharp } from '@mui/icons-material'
import { StaticDateRangePicker, LocalizationProvider, CalendarPickerSkeleton, DatePicker } from '@mui/lab'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { getData, setFeatureFlag } from '../../api/dataSlice'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { setTimelapseActions } from '../chatbot/ChatManager'
import { ConditionalWrapper, StyledSlider, getPresetRanges, parseBotDate, rangeOverlaps } from '../Utilities'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { LenientDayjsAdapter } from '../Utilities'
import i18next from 'i18next'
import CameraSelection from '../controls/CameraSelection'
import DownloadButton from '../controls/DownloadButton'

dayjs.extend(isBetween)

// gated by TIMELAPSE route access; users without the role land on "total" mode with toggle disabled; mobile flips `mysiteView` to 'timelapse' instead of expanding
export default function TimelapseAccordion({ expanded, setExpanded, mysiteView, onMysiteViewChange, setFooterOpen, timestamp, shouldDisableDate, loading: isLoadingParent }) {

    const { t } = useTranslation()
    const { hasRole, hasRouteAccess, isLoggedIn } = useKeycloak()
    const { isPortrait } = useMobileOrientation()
    const { source, download, generation, siderWidth } = useSelector(getData)
    const { CAMERAS, DEBUG, LAYOUT: { TITLE }, ROUTES: { TIMELAPSE } } = window.conf
    
    const [forceRefresh, setForceRefresh] = useState(true)
    const [selectedCamera, setSelectedCamera] = useState(CAMERAS?.[0]?.id)
    const [dateRange, setDateRange] = useState([timestamp, timestamp])
    const [predefinedRange, setPredefinedRange] = useState("")
    const [alertMessage, setAlertMessage] = useState(null)
    const [startDateValid, setStartDateValid] = useState(true)
    const [endDateValid, setEndDateValid] = useState(true)
    const [mode, setMode] = useState(hasRole(TIMELAPSE?.role) ? "custom" : "total")
    const [useRecommendedLength, setUseRecommendedLength] = useState(true)
    const [maxLength, setMaxLength] = useState(120)
    const [availableResolution, setAvailableResolution] = useState({ original: { width: 1920, height: 1080 }, reduced: { width: 1280, height: 720 } })
    const [selectedResolution, setSelectedResolution] = useState({ width: availableResolution.original.width, height: availableResolution.original.height })

    const dispatch = useDispatch()
    const refDownloadButton = useRef()
    const customRangeRef = useRef(false) // once true, the parent-timestamp sync effect leaves dateRange alone (user/chatbot took control)
    const daySize = 34
    const today = dayjs()
    const presetRanges = getPresetRanges(today)
    const isDownloadRangeExceeded = Math.abs(dateRange?.[0]?.diff(dateRange?.[1], 'day')) > 366
    const minDate = dayjs(source?.[0]?.[0]?.[0])
    const maxDate = dayjs(source?.[0]?.[source[0].length - 1]?.[0])
    const moreThanOneCam = CAMERAS.length > 1
    const hasTimelapse = hasRouteAccess(TIMELAPSE)
    const hasTimelapseRole = hasRole(TIMELAPSE?.role) // keeps the controls disabled when the accordion is only open
    const disableBaseControls = isLoadingParent || download?.loading || generation?.loading || selectedCamera == null // used by camera selector and total download button
    const disableControls = disableBaseControls || !hasTimelapseRole // used by mode toggle and custom control
    const disableDownloadCustomTimelapse = !source?.[0]?.length || selectedCamera == null || isDownloadRangeExceeded || !startDateValid || !endDateValid
    const cameraLabel = CAMERAS[selectedCamera]?.name
    ? `${CAMERAS[selectedCamera].name}`
        : moreThanOneCam
            ? `${t("events.camera")} ${CAMERAS[selectedCamera]?.id + 1}`
            : ""
            const title = TITLE ? (cameraLabel ? `${TITLE}_` : TITLE) : ""
    let startDate = dateRange?.[0]?.format("YY-MM-DD")
    const endDate = dateRange?.[1]?.format("YY-MM-DD")
    if (startDate && endDate && (startDate !== endDate)) startDate += "_" + endDate
    const targetFilenameTotal = `${title}${cameraLabel}${t("pages.timelapse")}`
    const targetFilenameCustom = targetFilenameTotal + `_${startDate}`
    const stateRef = useRef({}) // chatbot registry mirror — assigned below, after onChange* handlers exist

    // publish the synchronous role-based flag so the auto-expand hook can read it from Redux
    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasTimelapse', value: !!hasTimelapse }))
    }, [dispatch, hasTimelapse])

    // sync picker range to the parent-provided timestamp when it changes — but only until the user (or chatbot) has picked a custom range
    useEffect(() => {
        if (customRangeRef.current) return
        if (dateRange?.[0] !== timestamp || dateRange?.[1] !== timestamp) {
            setDateRange([timestamp, timestamp])
            setForceRefresh(!forceRefresh) // force remount of StaticDateRangePicker
        }
    }, [timestamp]) // eslint-disable-line react-hooks/exhaustive-deps

    // recompute available resolutions when camera changes
    useEffect(() => {
        let cancelled = false
        async function getImageResolution() {
            const img = new Image()
            img.src = CAMERAS[selectedCamera].image
            try {
                await img.decode()
                if (cancelled) return
                const original = { width: img.naturalWidth, height: img.naturalHeight }
                const reduced = { width: Math.round(img.naturalWidth / 1.5), height: Math.round(img.naturalHeight / 1.5) }
                DEBUG && console.log(`[TimelapseAccordion] resolution original ${original.width}x${original.height}, reduced ${reduced.width}x${reduced.height}`)
                setAvailableResolution({ original, reduced })
                setSelectedResolution({ width: original.width, height: original.height })
            } catch (error) {
                console.error("[TimelapseAccordion]", error)
            }
        }
        getImageResolution()
        return () => { cancelled = true }
    }, [selectedCamera]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (alertMessage) {
            refDownloadButton.current?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
    }, [alertMessage])

    useEffect(() => {
        if (isDownloadRangeExceeded) {
            setAlertMessage({ type: "warning", message: t("msg.select_limitation") })
        }
    }, [isDownloadRangeExceeded]) // eslint-disable-line react-hooks/exhaustive-deps

    const resetAlertMessage = () => { if (alertMessage) setAlertMessage(null) }

    const onChangeMode = (event, newValue) => {
        resetAlertMessage()
        if (newValue !== null) setMode(newValue)
    }

    const onChangeDateRange = (range) => {
        resetAlertMessage()
        if (range?.length) {
            setDateRange([range[0], range[1]])
            setPredefinedRange("")
            customRangeRef.current = true
        }
    }

    const onChangeStartDate = (start) => {
        resetAlertMessage()
        if (start?.isValid?.()) {
            setDateRange([start, dateRange[1]])
            setPredefinedRange("")
            customRangeRef.current = true
        }
    }

    const onChangeEndDate = (end) => {
        resetAlertMessage()
        if (end?.isValid?.()) {
            setDateRange([dateRange[0], end])
            setPredefinedRange("")
            customRangeRef.current = true
        }
    }

    const onChangePredefinedSpan = (event, newPredefinedSpan) => {
        resetAlertMessage()
        if (newPredefinedSpan && presetRanges[newPredefinedSpan]) {
            setDateRange(presetRanges[newPredefinedSpan])
            setPredefinedRange(newPredefinedSpan)
            setForceRefresh(!forceRefresh)
            customRangeRef.current = true
        }
    }

    const onChangeUseRecommendedLength = (event) => {
        resetAlertMessage()
        setUseRecommendedLength(event.target.checked)
    }

    const onChangeMaxLength = (event, newValue) => {
        resetAlertMessage()
        setMaxLength(newValue)
    }

    const onChangeSelectedResolution = (event, newValue) => {
        resetAlertMessage()
        if (newValue === 1) setSelectedResolution(availableResolution.reduced)
        else if (newValue === 2) setSelectedResolution(availableResolution.original)
    }

    const onAccordionChange = (event, newExpanded) => {
        if (isMobile) {
            // mobile: no accordion expansion — tap toggles only the content-area view
            const activating = mysiteView !== 'timelapse'
            if (activating) {
                setExpanded(false) // collapse any open accordion
                if (isPortrait) setFooterOpen(false) // close footer in portrait
            }
            onMysiteViewChange(activating ? 'timelapse' : null)
        } else {
            setExpanded(newExpanded ? 'timelapse' : false)
            onMysiteViewChange(newExpanded ? 'timelapse' : null)
        }
    }

    // chatbot registry — refs feed live state and live setters
    stateRef.current = {
        selectedCamera, mode, dateRange, predefinedRange, startDateValid, endDateValid,
        alertMessage, useRecommendedLength, maxLength, selectedResolution, availableResolution,
        setSelectedCamera, onChangeMode, onChangeDateRange, onChangePredefinedSpan,
        setUseRecommendedLength, setMaxLength, setSelectedResolution, setForceRefresh,
    }

    useEffect(() => {
        setTimelapseActions({
            getSelectedCameraId: () => stateRef.current.selectedCamera,
            getMode: () => stateRef.current.mode,
            getDateRange: () => {
                const [s, e] = stateRef.current.dateRange || []
                return (s?.isValid?.() && e?.isValid?.())
                    ? { start: s.format('YYYY-MM-DD'), end: e.format('YYYY-MM-DD') }
                    : null
            },
            getPredefinedRange: () => stateRef.current.predefinedRange || null,
            getStartDateValid: () => stateRef.current.startDateValid,
            getEndDateValid: () => stateRef.current.endDateValid,
            getAlertMessage: () => stateRef.current.alertMessage?.message || null,
            getUseRecommendedLength: () => stateRef.current.useRecommendedLength,
            getMaxLengthSec: () => stateRef.current.maxLength,
            getSelectedResolution: () => stateRef.current.selectedResolution,
            getAvailableResolutions: () => stateRef.current.availableResolution,
            setSelectedCameraId: (id) => stateRef.current.setSelectedCamera(id),
            setMode: (newMode) => stateRef.current.onChangeMode(null, newMode),
            setDateRange: (start, end) => {
                const s = parseBotDate(start)
                const e = parseBotDate(end)
                if (s && e) {
                    stateRef.current.onChangeDateRange([s, e])
                    stateRef.current.setForceRefresh(prev => !prev) // remount StaticDateRangePicker so the calendar jumps to the new month
                }
            },
            // accepts 'day' | 'week' | 'month' — wrapper expands it into the matching range
            setPredefinedRange: (range) => stateRef.current.onChangePredefinedSpan(null, range),
            setUseRecommendedLength: (checked) => stateRef.current.setUseRecommendedLength(!!checked),
            setMaxLengthSec: (seconds) => stateRef.current.setMaxLength(seconds),
            // accepts {width, height} or one of the named slots: 'original' | 'reduced'
            setSelectedResolution: (resolution) => {
                if (typeof resolution === 'string') {
                    const r = stateRef.current.availableResolution?.[resolution]
                    if (r) stateRef.current.setSelectedResolution(r)
                } else if (resolution?.width && resolution?.height) {
                    stateRef.current.setSelectedResolution(resolution)
                }
            },
        })
        return () => setTimelapseActions(null)
    }, [])

    return (
        <Accordion
            expanded={isMobile ? false : (expanded === 'timelapse')}
            disabled={!hasTimelapse}
            disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
            onChange={onAccordionChange}
        >
            <AccordionSummary
                {...(isMobile && { expandIcon: <ArrowForwardIosSharp sx={{ fontSize: '0.9rem', visibility: 'hidden' }} /> })}
                sx={isMobile && mysiteView === 'timelapse' ? { backgroundColor: '#E8EEF4' } : undefined}
                className={!isMobile ? "accordion-with-subtitle" : undefined}
            >
                {isMobile ? t("pages.timelapse") : (
                    <Box>
                        <Typography>{t("pages.timelapse")}</Typography>
                        <Typography className="accordion-subtitle">{`${t("download.custom")} & ${t("download.total")}`}</Typography>
                    </Box>
                )}
            </AccordionSummary>

            <AccordionDetails>
                <Box>
                    <ConditionalWrapper
                        condition={!hasTimelapseRole && !isLoggedIn()}
                        wrapper={children => (
                            <Tooltip title={t('msg.sign_in_to_unlock')} placement="top" arrow>
                                <span style={{ display: 'block' }}>{children}</span>
                            </Tooltip>
                        )}
                    >
                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            onChange={onChangeMode}
                            value={mode}
                            fullWidth
                            sx={{ pb: 2 }}
                            disabled={disableControls}
                        >
                            <ToggleButton value="custom">{t("download.custom")}</ToggleButton>
                            <ToggleButton value="total">{t("download.total")}</ToggleButton>
                        </ToggleButtonGroup>
                    </ConditionalWrapper>

                    {moreThanOneCam && (
                        <CameraSelection
                            value={selectedCamera}
                            onChange={setSelectedCamera}
                            disabled={disableBaseControls}
                        />
                    )}

                    {mode === "custom" && (
                        <Box>
                            <LocalizationProvider dateAdapter={LenientDayjsAdapter} locale={i18next.language}>
                                <Box sx={{ display: 'flex' }}>
                                    <DatePicker
                                        label={t("date.start_date")}
                                        inputFormat={t("date.format.short")}
                                        mask={t("date.mask.short")}
                                        disableMaskedInput
                                        renderInput={props =>
                                            <TextField
                                                {...props}
                                                sx={{
                                                    width: "100%",
                                                    userSelect: "none",
                                                    "& .MuiOutlinedInput-root": { "& > fieldset": { borderColor: "#4caf50" } },
                                                    "& .MuiInputAdornment-root": { margin: '-4px' }
                                                }}
                                            />
                                        }
                                        onError={reason => setStartDateValid(!reason)}
                                        disableFuture={true}
                                        value={dateRange[0]}
                                        onChange={onChangeStartDate}
                                        cancelText={t("actions.cancel")}
                                        toolbarTitle={t("date.select_date")}
                                        minDate={minDate}
                                        maxDate={dateRange[1]}
                                        loading={isLoadingParent}
                                        disabled={disableControls}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                    />

                                    <p style={{
                                        paddingLeft: 12,
                                        paddingRight: 12,
                                        height: "100%",
                                        alignItems: 'center',
                                        userSelect: 'none'
                                    }}>
                                        {t("date.to")}
                                    </p>

                                    <DatePicker
                                        label={t("date.end_date")}
                                        inputFormat={t("date.format.short")}
                                        mask={t("date.mask.short")}
                                        disableMaskedInput
                                        renderInput={props =>
                                            <TextField
                                                {...props}
                                                sx={{
                                                    width: "100%",
                                                    userSelect: "none",
                                                    "& .MuiOutlinedInput-root": { "& > fieldset": { borderColor: "#ed6c02" } },
                                                    "& .MuiInputAdornment-root": { margin: '-4px' }
                                                }}
                                            />
                                        }
                                        onError={reason => setEndDateValid(!reason)}
                                        disableFuture={true}
                                        value={dateRange[1]}
                                        onChange={onChangeEndDate}
                                        cancelText={t("actions.cancel")}
                                        toolbarTitle={t("date.select_date")}
                                        minDate={dateRange[0]}
                                        maxDate={maxDate}
                                        loading={isLoadingParent}
                                        disabled={disableControls}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                    />
                                </Box>
                            </LocalizationProvider>

                            <Box
                                sx={{
                                    "& > div": { minWidth: siderWidth, pt: 2 },
                                    "& > div > div": {
                                        border: "1px solid",
                                        borderColor: "#BDBDBD",
                                        borderRadius: "4px"
                                    },
                                    "& > div > div, & > div > div > div, & > div > div > div > div": {
                                        width: siderWidth - 2
                                    },
                                    "& .MuiTypography-caption": { width: daySize + 4, margin: 0 },
                                    "& .PrivatePickersSlideTransition-root": {
                                        minHeight: daySize * 7.2,
                                        overflow: "hidden"
                                    },
                                    '& .PrivatePickersSlideTransition-root [role="grid"]': { width: siderWidth },
                                    "& .MuiPickersDay-root": { width: daySize, height: daySize }
                                }}
                            >
                                <LocalizationProvider dateAdapter={LenientDayjsAdapter} locale={i18next.language} key={forceRefresh}>
                                    <StaticDateRangePicker
                                        calendars={1}
                                        displayStaticWrapperAs="desktop"
                                        mask={t("date.mask.full")}
                                        value={dateRange}
                                        onChange={onChangeDateRange}
                                        renderInput={() => {}}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        loading={isLoadingParent}
                                        disabled={disableControls}
                                        shouldDisableDate={shouldDisableDate}
                                        renderLoading={() => <CalendarPickerSkeleton />}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <ToggleButtonGroup
                                color="primary"
                                exclusive
                                onChange={onChangePredefinedSpan}
                                value={disableControls ? '' : predefinedRange}
                                fullWidth
                                orientation='vertical'
                                sx={{ py: 2 }}
                            >
                                <ToggleButton disabled={disableControls || !rangeOverlaps(presetRanges.day[0], presetRanges.day[1], minDate, maxDate)} value="day">{t("date.yesterday")}</ToggleButton>
                                <ToggleButton disabled={disableControls || !rangeOverlaps(presetRanges.week[0], presetRanges.week[1], minDate, maxDate)} value="week">{t("date.last_week")}</ToggleButton>
                                <ToggleButton disabled={disableControls || !rangeOverlaps(presetRanges.month[0], presetRanges.month[1], minDate, maxDate)} value="month">{t("date.last_month")}</ToggleButton>
                            </ToggleButtonGroup>

                            {/* length + quality sliders */}
                            <Box sx={{
                                px: 2,
                                py: 1,
                                borderRadius: "4px",
                                border: "1px solid rgba(0, 0, 0, 0.12)",
                                userSelect: "none",
                                mb: 2
                            }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={useRecommendedLength}
                                            onChange={onChangeUseRecommendedLength}
                                            disabled={disableControls}
                                        />
                                    }
                                    label={
                                        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {t("timelapse.use_recommended_length")}
                                        </Box>
                                    }
                                />

                                <Box sx={{ mt: 1, position: "relative" }}>
                                    <Typography sx={{ color: useRecommendedLength || disableControls ? "#bdbdbd" : "text.primary" }}>
                                        {`${t("timelapse.maximum_length")}: ${maxLength} ${t("timelapse.seconds")}`}
                                    </Typography>

                                    <Box sx={{ p: "0 10px 10px" }}>
                                        <StyledSlider
                                            aria-label={t("timelapse.maximum_length")}
                                            value={maxLength}
                                            getAriaValueText={(value) => `${value}%`}
                                            step={10}
                                            min={10}
                                            max={120}
                                            disabled={useRecommendedLength || disableControls}
                                            valueLabelDisplay="auto"
                                            marks={[{ value: 10, label: "10" }, { value: 120, label: "120" }]}
                                            onChange={onChangeMaxLength}
                                        />

                                        <Box sx={{
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            position: "absolute",
                                            left: 0,
                                            bottom: 10
                                        }}>
                                            <Typography sx={{ fontSize: "0.875rem", color: useRecommendedLength || disableControls ? "#bdbdbd" : "text.primary" }}>10</Typography>
                                            <Typography sx={{ fontSize: "0.875rem", color: useRecommendedLength || disableControls ? "#bdbdbd" : "text.primary" }}>120</Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box sx={{ mt: 2, position: "relative" }}>
                                    <Typography sx={{ color: disableControls ? "#bdbdbd" : "text.primary" }}>
                                        {t("timelapse.quality")}: {selectedResolution.width}x{selectedResolution.height}
                                    </Typography>

                                    <Box sx={{ padding: "0 10px" }}>
                                        <StyledSlider
                                            aria-label={t("timelapse.quality")}
                                            value={selectedResolution.width === availableResolution.reduced.width ? 1 : 2}
                                            getAriaValueText={() => `${selectedResolution.width}x${selectedResolution.height}`}
                                            valueLabelFormat={() => `${selectedResolution.width}x${selectedResolution.height}`}
                                            disabled={disableControls}
                                            step={1}
                                            min={1}
                                            max={2}
                                            valueLabelDisplay="auto"
                                            marks={[
                                                { value: 1, label: `${availableResolution.reduced.width}x${availableResolution.reduced.height}` },
                                                { value: 2, label: `${availableResolution.original.width}x${availableResolution.original.width}` }
                                            ]}
                                            onChange={onChangeSelectedResolution}
                                        />

                                        <Box sx={{
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            position: "absolute",
                                            left: 0,
                                            bottom: 0
                                        }}>
                                            <Typography sx={{ fontSize: "0.875rem", color: disableControls ? "#bdbdbd" : "text.primary" }}>{t("timelapse.reduced")}</Typography>
                                            <Typography sx={{ fontSize: "0.875rem", color: disableControls ? "#bdbdbd" : "text.primary" }}>{t("timelapse.original")}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {alertMessage && (
                        <Box sx={{ mb: 2 }}>
                            <Alert
                                variant="filled"
                                severity={alertMessage.type}
                                sx={{ alignItems: "center", userSelect: "none" }}
                            >
                                {alertMessage.message || t("msg.unknown_error")}
                            </Alert>
                        </Box>
                    )}

                    {mode === "total" && (
                        <DownloadButton
                            sourceUrl={CAMERAS[selectedCamera]?.timelapse}
                            targetFilename={`${targetFilenameTotal}.mp4`}
                            fileType="video"
                            disabled={disableBaseControls}
                        />
                    )}

                    {mode === "custom" && (
                        <DownloadButton
                            targetFilename={`${targetFilenameCustom}.mp4`}
                            disabled={disableControls || disableDownloadCustomTimelapse}
                            selectedCamera={selectedCamera}
                            dateRange={dateRange}
                            setAlertMessage={setAlertMessage}
                            ref={refDownloadButton}
                            selectedResolution={selectedResolution}
                            maxLength={useRecommendedLength ? 0 : maxLength}
                        />
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
